import {
  createActorParticipantId,
  createUserParticipantId,
  resolveParticipantId,
} from '../../../domain'
import {
  PRESENCE_EVENT_TYPES,
  RTC_SESSION_EVENT_TYPES,
} from '../../../ports'
import { RTC_PROVIDERS } from '../../../providerIds'
import { isHumanParticipantIdentityKeyPrefix } from '../participantIdentityContract'
import { createAgoraRtcSessionHandle } from './createAgoraRtcSessionHandle'

function createEventBus() {
  const listeners = new Set()

  return {
    emit(event) {
      listeners.forEach((listener) => {
        listener(event)
      })
    },
    subscribe(listener) {
      listeners.add(listener)

      return () => {
        listeners.delete(listener)
      }
    },
    clear() {
      listeners.clear()
    },
  }
}

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function normalizeUidKey(uid) {
  if (uid === null || uid === undefined || uid === '') {
    return ''
  }

  const numericUid = Number(uid)

  if (Number.isFinite(numericUid)) {
    return String(numericUid)
  }

  return String(uid).trim()
}

function compareUidKeys(leftUid, rightUid) {
  const leftNumericUid = Number(leftUid)
  const rightNumericUid = Number(rightUid)

  if (Number.isFinite(leftNumericUid) && Number.isFinite(rightNumericUid)) {
    return leftNumericUid - rightNumericUid
  }

  return String(leftUid).localeCompare(String(rightUid))
}

function normalizePresenceMutedState(value, fallbackValue = false) {
  if (value === '1' || value === 1 || value === true) {
    return true
  }

  if (value === '0' || value === 0 || value === false) {
    return false
  }

  return fallbackValue
}

function parseUsernameMetadata(username) {
  const trimmedUsername = normalizeOptionalString(username, '')

  if (!trimmedUsername) {
    return {
      rawUsername: '',
      displayName: '',
      role: null,
      profilePicId: null,
      identityKey: null,
    }
  }

  try {
    const parsedUsername = JSON.parse(trimmedUsername)

    if (parsedUsername && typeof parsedUsername === 'object' && !Array.isArray(parsedUsername)) {
      return {
        rawUsername: trimmedUsername,
        displayName:
          normalizeOptionalString(
            parsedUsername?.name ?? parsedUsername?.displayName,
            trimmedUsername,
          ) || trimmedUsername,
        role: normalizeOptionalString(parsedUsername?.role),
        profilePicId: normalizeOptionalString(parsedUsername?.profilePicId),
        identityKey: normalizeOptionalString(parsedUsername?.identityKey),
      }
    }
  } catch (error) {
    return {
      rawUsername: trimmedUsername,
      displayName: trimmedUsername,
      role: null,
      profilePicId: null,
      identityKey: null,
    }
  }

  return {
    rawUsername: trimmedUsername,
    displayName: trimmedUsername,
    role: null,
    profilePicId: null,
    identityKey: null,
  }
}

function parseIdentityKey(identityKey) {
  const normalizedIdentityKey = normalizeOptionalString(identityKey)

  if (!normalizedIdentityKey || !normalizedIdentityKey.includes(':')) {
    return null
  }

  const [rawPrefix, ...rawStableIdParts] = normalizedIdentityKey.split(':')
  const prefix = normalizeOptionalString(rawPrefix)
  const stableId = normalizeOptionalString(rawStableIdParts.join(':'))

  if (!prefix || !stableId) {
    return null
  }

  return {
    prefix,
    stableId,
  }
}

function createAgoraTransport(uid, metadata = {}, extraTransport = {}) {
  const uidKey = normalizeUidKey(uid)

  return {
    providerId: RTC_PROVIDERS.agora,
    uid: uidKey,
    rtcUid: uidKey,
    rawUsername: metadata?.rawUsername || '',
    identityKey: metadata?.identityKey || null,
    profilePicId: normalizeOptionalString(metadata?.profilePicId),
    ...extraTransport,
  }
}

function normalizeLocalParticipantIdentity(localParticipantIdentity = {}) {
  const participantId = resolveParticipantId(localParticipantIdentity)

  if (!participantId) {
    return null
  }

  return {
    participantId,
    userId: normalizeOptionalString(localParticipantIdentity?.userId),
    actorType: normalizeOptionalString(localParticipantIdentity?.actorType),
    actorId: normalizeOptionalString(
      localParticipantIdentity?.actorId ?? localParticipantIdentity?.stableActorId,
    ),
    displayName: normalizeOptionalString(localParticipantIdentity?.displayName),
    profilePicId: normalizeOptionalString(localParticipantIdentity?.profilePicId),
    role: normalizeOptionalString(localParticipantIdentity?.role),
  }
}

function deriveParticipantIdentity({
  uid,
  metadata = {},
  isLocal = false,
  localParticipantIdentity = null,
  allowTransportFallback = true,
}) {
  const normalizedLocalIdentity = normalizeLocalParticipantIdentity(localParticipantIdentity)

  if (isLocal && normalizedLocalIdentity) {
    return normalizedLocalIdentity
  }

  const parsedIdentityKey = parseIdentityKey(metadata?.identityKey)

  if (parsedIdentityKey) {
    if (isHumanParticipantIdentityKeyPrefix(parsedIdentityKey.prefix)) {
      return {
        participantId: createUserParticipantId(parsedIdentityKey.stableId),
        userId: parsedIdentityKey.stableId,
        actorType: 'user',
        actorId: parsedIdentityKey.stableId,
        role: normalizeOptionalString(metadata?.role, parsedIdentityKey.prefix),
        displayName: normalizeOptionalString(metadata?.displayName),
        profilePicId: normalizeOptionalString(metadata?.profilePicId),
      }
    }

    return {
      participantId: createActorParticipantId(
        parsedIdentityKey.prefix,
        parsedIdentityKey.stableId,
      ),
      actorType: parsedIdentityKey.prefix,
      actorId: parsedIdentityKey.stableId,
      role: normalizeOptionalString(metadata?.role, parsedIdentityKey.prefix),
      displayName: normalizeOptionalString(metadata?.displayName),
      profilePicId: normalizeOptionalString(metadata?.profilePicId),
    }
  }

  if (!allowTransportFallback) {
    return null
  }

  const uidKey = normalizeUidKey(uid)

  if (!uidKey) {
    return null
  }

  return {
    participantId: createActorParticipantId('agora-uid', uidKey),
    actorType: 'agora-uid',
    actorId: uidKey,
    role: normalizeOptionalString(metadata?.role, 'participant'),
    displayName: normalizeOptionalString(metadata?.displayName),
    profilePicId: normalizeOptionalString(metadata?.profilePicId),
    transportIdentityFallback: true,
  }
}

function normalizeSnapshotParticipant(snapshotParticipant = {}, options = {}) {
  const uidKey = normalizeUidKey(snapshotParticipant?.uid)

  if (!uidKey) {
    return null
  }

  const parsedMetadata =
    snapshotParticipant?.metadata && typeof snapshotParticipant.metadata === 'object'
      ? snapshotParticipant.metadata
      : parseUsernameMetadata(snapshotParticipant?.rawUsername)
  const metadata = {
    ...parsedMetadata,
    displayName:
      normalizeOptionalString(parsedMetadata?.displayName) ||
      normalizeOptionalString(snapshotParticipant?.displayName),
    role:
      normalizeOptionalString(parsedMetadata?.role) ||
      normalizeOptionalString(snapshotParticipant?.role),
  }
  const identity = deriveParticipantIdentity({
    uid: snapshotParticipant?.uid,
    metadata,
    isLocal: options?.isLocal === true,
    localParticipantIdentity: options?.localParticipantIdentity,
    allowTransportFallback: options?.allowTransportFallback !== false,
  })

  if (!identity?.participantId) {
    return null
  }

  return {
    participantId: identity.participantId,
    userId: identity?.userId || null,
    actorType: identity?.actorType || null,
    actorId: identity?.actorId || null,
    displayName:
      identity?.displayName ||
      normalizeOptionalString(metadata?.displayName) ||
      (options?.isLocal ? 'You' : `Participant ${uidKey}`),
    profilePicId:
      identity?.profilePicId || normalizeOptionalString(metadata?.profilePicId),
    role:
      identity?.role ||
      normalizeOptionalString(metadata?.role) ||
      (options?.isLocal ? 'local' : 'participant'),
    isLocal: options?.isLocal === true,
    connectionState: 'connected',
    audioMuted: snapshotParticipant?.hasAudio !== true,
    videoMuted: snapshotParticipant?.hasVideo !== true,
    screenSharing: snapshotParticipant?.isScreenShare === true,
    videoHandle: snapshotParticipant?.videoTrack || null,
    renderKey:
      normalizeOptionalString(snapshotParticipant?.renderKey) ||
      `agora:${uidKey}:${snapshotParticipant?.isScreenShare ? 'screen' : 'video'}`,
    transport: createAgoraTransport(snapshotParticipant?.uid, metadata, {
      transportIdentityFallback: identity?.transportIdentityFallback === true,
    }),
  }
}

function normalizePresenceParticipant({
  uid,
  username,
  presenceState = {},
  localParticipantIdentity = null,
}) {
  const metadata = parseUsernameMetadata(username)
  const identity = deriveParticipantIdentity({
    uid,
    metadata,
    localParticipantIdentity,
    allowTransportFallback: true,
  })

  if (!identity?.participantId) {
    return null
  }

  return {
    participantId: identity.participantId,
    userId: identity?.userId || null,
    actorType: identity?.actorType || null,
    actorId: identity?.actorId || null,
    displayName:
      identity?.displayName ||
      normalizeOptionalString(metadata?.displayName) ||
      `Participant ${normalizeUidKey(uid)}`,
    profilePicId:
      identity?.profilePicId || normalizeOptionalString(metadata?.profilePicId),
    role: identity?.role || metadata?.role || 'participant',
    isLocal: false,
    connectionState: 'connected',
    audioMuted: normalizePresenceMutedState(presenceState?.audioMuted, false),
    videoMuted: normalizePresenceMutedState(presenceState?.videoMuted, true),
    screenSharing: false,
    videoHandle: null,
    renderKey: `agora:${normalizeUidKey(uid)}:presence`,
    transport: createAgoraTransport(uid, metadata),
  }
}

export function createAgoraVideoBinderPort() {
  return {
    getRenderKey(participant = {}) {
      return (
        normalizeOptionalString(participant?.renderKey) ||
        normalizeOptionalString(participant?.id) ||
        normalizeOptionalString(participant?.participantId) ||
        normalizeOptionalString(participant?.transport?.uid) ||
        'agora:participant'
      )
    },
    async bindVideo({ videoHandle, element }) {
      if (!videoHandle || !element || typeof videoHandle.play !== 'function') {
        return
      }

      await Promise.resolve(videoHandle.play(element))
    },
    async unbindVideo({ videoHandle, participant }) {
      const resolvedVideoHandle = videoHandle ?? participant?.videoHandle ?? null

      if (!resolvedVideoHandle || typeof resolvedVideoHandle.stop !== 'function') {
        return
      }

      await Promise.resolve(resolvedVideoHandle.stop())
    },
  }
}

export function createAgoraRtcPortBundle({
  initialConfig = {},
  sessionHandle = null,
  localParticipantIdentity = null,
} = {}) {
  const ownsSessionHandle = !sessionHandle
  const resolvedSessionHandle =
    sessionHandle || createAgoraRtcSessionHandle({ initialConfig })
  const rtcEvents = createEventBus()
  const presenceEvents = createEventBus()
  const participantsByUidKey = new Map()
  const metadataByUidKey = new Map()
  const rtcMembershipByUidKey = new Set()
  const presenceMembershipByUidKey = new Set()
  let runtimeCommands = {
    startScreenShare: null,
    stopScreenShare: null,
  }

  let currentLocalParticipantIdentity = localParticipantIdentity || null

  function syncScreenShareConfigState(active) {
    const currentConfig =
      typeof resolvedSessionHandle?.getConfig === 'function'
        ? resolvedSessionHandle.getConfig()
        : null
    const currentEnabled =
      currentConfig?.rtcProps?.enableScreensharing === true

    if (active === currentEnabled) {
      return Promise.resolve(active)
    }

    return Promise.resolve(
      active === true
        ? resolvedSessionHandle.startScreenshare()
        : resolvedSessionHandle.stopScreenshare(),
    )
  }

  function rememberMetadata(uid, metadata) {
    const uidKey = normalizeUidKey(uid)

    if (!uidKey || !metadata) {
      return
    }

    metadataByUidKey.set(uidKey, metadata)
  }

  function readMetadata(uid) {
    const uidKey = normalizeUidKey(uid)

    if (!uidKey) {
      return null
    }

    return metadataByUidKey.get(uidKey) || null
  }

  function rememberParticipant(participant = {}) {
    const uidKey = normalizeUidKey(
      participant?.transport?.uid ?? participant?.transport?.rtcUid,
    )

    if (!uidKey) {
      return
    }

    participantsByUidKey.set(uidKey, participant)
  }

  function forgetParticipant(uid) {
    const uidKey = normalizeUidKey(uid)

    if (!uidKey) {
      return
    }

    participantsByUidKey.delete(uidKey)
  }

  function rememberRtcMembership(uid) {
    const uidKey = normalizeUidKey(uid)

    if (!uidKey) {
      return
    }

    rtcMembershipByUidKey.add(uidKey)
  }

  function forgetRtcMembership(uid) {
    const uidKey = normalizeUidKey(uid)

    if (!uidKey) {
      return
    }

    rtcMembershipByUidKey.delete(uidKey)
  }

  function rememberPresenceMembership(uid) {
    const uidKey = normalizeUidKey(uid)

    if (!uidKey) {
      return
    }

    presenceMembershipByUidKey.add(uidKey)
  }

  function forgetPresenceMembership(uid) {
    const uidKey = normalizeUidKey(uid)

    if (!uidKey) {
      return
    }

    presenceMembershipByUidKey.delete(uidKey)
  }

  function isTrackedParticipantUid(uid) {
    const uidKey = normalizeUidKey(uid)

    if (!uidKey) {
      return false
    }

    return (
      rtcMembershipByUidKey.has(uidKey) || presenceMembershipByUidKey.has(uidKey)
    )
  }

  function buildTrackedParticipantsSnapshot() {
    return Array.from(participantsByUidKey.entries())
      .sort(([leftUid], [rightUid]) => compareUidKeys(leftUid, rightUid))
      .map(([, participant]) => participant)
      .sort((leftParticipant, rightParticipant) => {
        if (leftParticipant?.isLocal !== rightParticipant?.isLocal) {
          return leftParticipant?.isLocal ? -1 : 1
        }

        return compareUidKeys(
          leftParticipant?.transport?.uid ?? leftParticipant?.transport?.rtcUid,
          rightParticipant?.transport?.uid ?? rightParticipant?.transport?.rtcUid,
        )
      })
  }

  function emitParticipantSnapshot(snapshot = {}) {
    const rtcSnapshotUidKeys = new Set()
    const snapshotParticipants = [
      ...(snapshot?.localParticipant ? [{ ...snapshot.localParticipant, isLocal: true }] : []),
      ...((Array.isArray(snapshot?.remoteParticipants)
        ? snapshot.remoteParticipants
        : []
      ).map((participant) => ({ ...participant, isLocal: false }))),
    ]

    snapshotParticipants.forEach((snapshotParticipant) => {
      const uidKey = normalizeUidKey(snapshotParticipant?.uid)

      if (!uidKey) {
        return
      }

      const metadata = parseUsernameMetadata(
        snapshotParticipant?.rawUsername ||
          readMetadata(snapshotParticipant?.uid)?.rawUsername ||
          '',
      )

      if (metadata?.rawUsername) {
        rememberMetadata(snapshotParticipant?.uid, metadata)
      }

      const participant = normalizeSnapshotParticipant(
        {
          ...snapshotParticipant,
          metadata,
        },
        {
          isLocal: snapshotParticipant?.isLocal === true,
          localParticipantIdentity: currentLocalParticipantIdentity,
        },
      )

      if (!participant) {
        if (!isTrackedParticipantUid(snapshotParticipant?.uid)) {
          forgetParticipant(snapshotParticipant?.uid)
        }
        return
      }

      rtcSnapshotUidKeys.add(uidKey)
      rememberRtcMembership(snapshotParticipant?.uid)
      rememberParticipant(participant)
    })

    Array.from(rtcMembershipByUidKey).forEach((uidKey) => {
      if (!rtcSnapshotUidKeys.has(uidKey)) {
        rtcMembershipByUidKey.delete(uidKey)
      }
    })

    Array.from(participantsByUidKey.keys()).forEach((uidKey) => {
      if (!rtcMembershipByUidKey.has(uidKey) && !presenceMembershipByUidKey.has(uidKey)) {
        participantsByUidKey.delete(uidKey)
      }
    })

    rtcEvents.emit({
      type: RTC_SESSION_EVENT_TYPES.participantsChanged,
      participants: buildTrackedParticipantsSnapshot(),
    })
  }

  const rtcSession = {
    async join(config = {}) {
      if (config && Object.keys(config).length > 0) {
        resolvedSessionHandle.updateConfig(config)
      }

      return resolvedSessionHandle.join()
    },
    async leave() {
      return resolvedSessionHandle.leave()
    },
    async setLocalAudioEnabled(enabled) {
      return resolvedSessionHandle.setAudioEnabled(enabled)
    },
    async setLocalVideoEnabled(enabled) {
      return resolvedSessionHandle.setVideoEnabled(enabled)
    },
    async setDevices(devices = {}) {
      const deviceTasks = []

      if (devices?.cameraDeviceId !== undefined) {
        deviceTasks.push(resolvedSessionHandle.switchCamera(devices.cameraDeviceId))
      }

      if (devices?.microphoneDeviceId !== undefined) {
        deviceTasks.push(
          resolvedSessionHandle.switchMicrophone(devices.microphoneDeviceId),
        )
      }

      if (devices?.playbackDeviceId !== undefined) {
        deviceTasks.push(
          resolvedSessionHandle.setPlaybackDevice(devices.playbackDeviceId),
        )
      }

      await Promise.all(deviceTasks)
      return true
    },
    async startScreenShare() {
      if (typeof runtimeCommands.startScreenShare !== 'function') {
        throw new Error('[live/rtc/agora] screen-share runtime not ready')
      }

      let runtimeResult

      try {
        runtimeResult = runtimeCommands.startScreenShare()
      } catch (error) {
        await resolvedSessionHandle.stopScreenshare().catch(() => {})
        throw error
      }

      const configPromise = syncScreenShareConfigState(true)

      return Promise.resolve(runtimeResult).then(
        async (result) => {
          await configPromise
          return result
        },
        async (error) => {
          await resolvedSessionHandle.stopScreenshare().catch(() => {})
          throw error
        },
      )
    },
    async stopScreenShare() {
      if (typeof runtimeCommands.stopScreenShare !== 'function') {
        throw new Error('[live/rtc/agora] screen-share runtime not ready')
      }

      let runtimeResult

      try {
        runtimeResult = runtimeCommands.stopScreenShare()
      } catch (error) {
        await resolvedSessionHandle.stopScreenshare().catch(() => {})
        throw error
      }

      const configPromise = syncScreenShareConfigState(false)

      return Promise.resolve(runtimeResult).finally(async () => {
        await configPromise
      })
    },
    subscribe(listener) {
      return rtcEvents.subscribe(listener)
    },
  }

  const presence = {
    async connect(config = {}) {
      if (config && Object.keys(config).length > 0) {
        resolvedSessionHandle.updateConfig(config)
      }

      return true
    },
    async disconnect() {
      return true
    },
    async publishLocalParticipantMetadata(metadata = {}) {
      currentLocalParticipantIdentity = {
        ...(currentLocalParticipantIdentity || {}),
        ...metadata,
      }

      const localParticipant = normalizeLocalParticipantIdentity(
        currentLocalParticipantIdentity,
      )

      if (localParticipant) {
        presenceEvents.emit({
          type: PRESENCE_EVENT_TYPES.participantMetadataChanged,
          participant: {
            ...localParticipant,
            isLocal: true,
          },
          metadata: {
            ...localParticipant,
            isLocal: true,
          },
          admitToRoster: false,
        })
      }

      return localParticipant
    },
    subscribe(listener) {
      return presenceEvents.subscribe(listener)
    },
  }

  const compatibilityCallbacks = {
    'register-runtime-commands': (commands) => {
      runtimeCommands = {
        startScreenShare:
          typeof commands?.startScreenShare === 'function'
            ? commands.startScreenShare
            : null,
        stopScreenShare:
          typeof commands?.stopScreenShare === 'function'
            ? commands.stopScreenShare
            : null,
      }
    },
    'screen-share-state-changed': (active) => {
      syncScreenShareConfigState(active === true).catch(() => {})
    },
    'participants-updated': (snapshot) => {
      emitParticipantSnapshot(snapshot)
    },
    'connection-state-change': (sessionState, previousState, reason) => {
      rtcEvents.emit({
        type: RTC_SESSION_EVENT_TYPES.stateChanged,
        sessionState,
        previousState,
        reason,
      })
    },
    'user-left': (user, reason) => {
      const uidKey = normalizeUidKey(user?.uid)
      const participant = uidKey ? participantsByUidKey.get(uidKey) || null : null

      if (!participant) {
        return
      }

      forgetRtcMembership(user?.uid)
      participantsByUidKey.delete(uidKey)
      rtcEvents.emit({
        type: RTC_SESSION_EVENT_TYPES.participantsChanged,
        changeType: 'left',
        participant,
        transport: participant.transport,
        reason,
      })
    },
    'user-unpublished': () => {
      // Media changes flow through the participant snapshot so unpublish never
      // becomes a roster-removal path inside the adapter bridge.
    },
    'rtm-user-joined': (uid, username, presenceState = {}) => {
      rememberMetadata(uid, parseUsernameMetadata(username))
      rememberPresenceMembership(uid)

      const participant = normalizePresenceParticipant({
        uid,
        username,
        presenceState,
        localParticipantIdentity: currentLocalParticipantIdentity,
      })

      if (!participant) {
        return
      }

      rememberParticipant(participant)
      presenceEvents.emit({
        type: PRESENCE_EVENT_TYPES.participantMetadataChanged,
        participant,
        metadata: participant,
        admitToRoster: true,
      })
    },
    'rtm-user-left': (uid) => {
      const uidKey = normalizeUidKey(uid)
      const participant = uidKey ? participantsByUidKey.get(uidKey) || null : null
      const hasRtcMembership = uidKey ? rtcMembershipByUidKey.has(uidKey) : false

      forgetPresenceMembership(uid)

      if (!hasRtcMembership) {
        forgetParticipant(uid)
      }

      if (!hasRtcMembership) {
        presenceEvents.emit({
          type: PRESENCE_EVENT_TYPES.participantLeft,
          participant,
          transport: createAgoraTransport(uid),
        })
      }
    },
  }

  return {
    sessionHandle: resolvedSessionHandle,
    rtcSession,
    presence,
    videoBinder: createAgoraVideoBinderPort(),
    compatibilityCallbacks,
    async destroy() {
      rtcEvents.clear()
      presenceEvents.clear()
      participantsByUidKey.clear()
      metadataByUidKey.clear()
      rtcMembershipByUidKey.clear()
      presenceMembershipByUidKey.clear()
      runtimeCommands = {
        startScreenShare: null,
        stopScreenShare: null,
      }

      if (ownsSessionHandle) {
        await resolvedSessionHandle.destroy()
      }
    },
  }
}
